import "../index.css";
import { useEffect } from "react";
import Footer from "../components/Footer";
//import { useAuth } from "../auth/context";
import { PageNav } from "../components/PageNav";
import ReactMarkdown from "react-markdown";

export default function Terms() {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const markdownContent = `
# Terms of Service

**Last Updated: February 26, 2026**

## 1. Agreement to Terms

Welcome to BStrong Performance. These Terms of Service ("Terms") govern your access to and use of the BStrong Performance website and any related services (collectively, the "Service") operated by BStrong Performance ("BStrong Performance," "we," "us," or "our").

**By accessing or using our Service, you agree to be bound by these Terms.** If you do not agree to these Terms, do not use our Service.

## 2. Description of Services

BStrong Performance provides professional leadership development and coaching services, including:
- Executive and leadership coaching for individuals and teams
- Leadership development programs, workshops, and facilitation
- Organizational assessment and alignment engagements
- Use of credentialed assessment tools (including MBTI, Hogan, Insights, EQ-i2.0, Risk Type, and others)
- Online resources, educational content, and program materials

**Important:** BStrong Performance's services are developmental and educational in nature. We do not provide licensed psychological therapy, legal advice, financial advice, or medical services. Coaching is distinct from therapy and is not a substitute for professional mental health treatment.

## 3. Eligibility

To engage our services, you must:
- Be at least **18 years of age**
- Have the legal capacity to enter into a binding agreement
- Not be prohibited from receiving our services under applicable law

By using our Service, you represent and warrant that you meet all eligibility requirements.

## 4. Engagement and Service Agreements

### 4.1 Service Proposals and Agreements

Specific coaching, facilitation, or consulting engagements are governed by a separate written service agreement or statement of work ("Engagement Agreement") executed between you (or your organization) and BStrong Performance. In the event of any conflict between these Terms and an Engagement Agreement, the Engagement Agreement will control.

### 4.2 Account Registration

Certain areas of our Service may require you to create an account. You agree to:
- Provide accurate and complete registration information
- Keep your account information current
- Maintain the confidentiality of your login credentials
- Notify us promptly of any unauthorized access to your account

You are solely responsible for all activity that occurs under your account.

### 4.3 Account Termination

You may close your account at any time by contacting us. We may suspend or terminate your account if you violate these Terms, engage in fraudulent or harmful conduct, or as otherwise required by law.

## 5. Fees and Payment Terms

### 5.1 Fees

Fees for services are set forth in the applicable Engagement Agreement, proposal, or as displayed on our website. All fees are in U.S. Dollars (USD) unless otherwise stated.

### 5.2 Payment

Payment terms are specified in your Engagement Agreement. Unless otherwise agreed:
- Invoices are due within 30 days of the invoice date
- Late payments may be subject to a late fee of 1.5% per month on the outstanding balance

### 5.3 Cancellations and Refunds

Cancellation and refund terms are governed by your Engagement Agreement. In the absence of a specific agreement:
- Cancellations made more than 7 business days before a scheduled session will receive a full refund
- Cancellations made within 7 business days of a scheduled session may be subject to a cancellation fee of up to 50% of the session fee
- No refunds will be issued for sessions already delivered

### 5.4 Taxes

You are responsible for all applicable taxes related to your use of our services, except for taxes based on our net income.

## 6. Confidentiality

### 6.1 Coaching Confidentiality

BStrong Performance treats information shared during coaching and facilitation engagements as confidential. We will not disclose your personal information to third parties without your consent, except:
- As required by law or legal process
- To prevent imminent harm to you or others
- As otherwise agreed in your Engagement Agreement (e.g., summary reporting to a sponsoring organization)

### 6.2 Your Confidentiality Obligations

You agree to keep confidential any proprietary methodologies, materials, tools, or information shared with you by BStrong Performance that are designated as confidential or that you reasonably should understand to be confidential.

## 7. Intellectual Property Rights

### 7.1 Our Content and Materials

All content, curricula, frameworks, assessment interpretations, training materials, and methodologies provided by BStrong Performance are owned by or licensed to BStrong Performance and are protected by copyright, trademark, and other intellectual property laws.

### 7.2 Limited License

We grant you a limited, non-exclusive, non-transferable license to use materials provided to you solely for your own personal development or internal organizational use, as specified in your Engagement Agreement. You may not:
- Reproduce, distribute, or publicly display our materials without written permission
- Use our materials for commercial purposes or to deliver services to third parties
- Remove or alter any copyright, trademark, or proprietary notices

### 7.3 Assessment Tools

Certain assessment tools used in our programs are owned by third-party publishers and are subject to their respective licensing terms. Your use of those tools is governed accordingly.

### 7.4 Feedback

Any feedback, suggestions, or ideas you provide to us about our services may be used by BStrong Performance without obligation or compensation to you.

## 8. Acceptable Use

You agree to use our Service only for lawful purposes and in accordance with these Terms. You agree NOT to:
- Use the Service in any way that violates applicable laws or regulations
- Impersonate any person or entity or misrepresent your affiliation with any person or entity
- Transmit any harmful, offensive, or disruptive content
- Attempt to gain unauthorized access to any part of our systems or networks
- Use automated tools to access or scrape our website without written permission
- Reproduce or distribute our proprietary content or materials without authorization

## 9. Disclaimer of Warranties

THE SERVICE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

BStrong Performance does not warrant that:
- The Service will be uninterrupted, error-free, or secure
- Any particular outcome will result from coaching or training engagements
- Information or content on our website is complete, accurate, or current

**Coaching and leadership development results vary by individual. BStrong Performance makes no guarantees regarding specific professional, organizational, or personal outcomes.**

## 10. Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, BSTRONG PERFORMANCE, ITS PRINCIPALS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, REVENUE, OR BUSINESS OPPORTUNITIES, ARISING FROM OR RELATED TO YOUR USE OF THE SERVICE OR ANY ENGAGEMENT WITH US.

IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNTS YOU PAID TO BSTRONG PERFORMANCE IN THE 12 MONTHS PRECEDING THE CLAIM.

These limitations apply even if we have been advised of the possibility of such damages, and reflect a fundamental part of the basis of the bargain between you and BStrong Performance.

## 11. Indemnification

You agree to indemnify, defend, and hold harmless BStrong Performance, its principals, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising from:
- Your use or misuse of the Service
- Your violation of these Terms
- Your violation of any applicable law or regulation
- Your infringement of any third-party rights

## 12. Dispute Resolution

### 12.1 Informal Resolution

Before initiating any formal dispute, you agree to contact us at brian@bstrongperformance.com to attempt to resolve the matter informally. We will endeavor to respond within 30 days.

### 12.2 Governing Law

These Terms are governed by the laws of the United States and the state in which BStrong Performance operates, without regard to conflict of law principles.

### 12.3 Dispute Forum

Any dispute not resolved informally shall be submitted to binding arbitration administered by the American Arbitration Association under its Commercial Arbitration Rules, or resolved in the appropriate state or federal court. The parties waive the right to a jury trial.

### 12.4 Class Action Waiver

All disputes will be resolved on an individual basis. You waive any right to participate in a class action or class-wide arbitration proceeding.

## 13. Modifications to These Terms

We reserve the right to update these Terms at any time. When we do:
- We will update the "Last Updated" date at the top of this page
- We will notify you of material changes via email or a notice on our website
- Continued use of the Service after changes take effect constitutes acceptance of the updated Terms

## 14. Service Modifications

We reserve the right to modify, suspend, or discontinue any aspect of our Service at any time. We will provide reasonable notice of material changes when possible.

## 15. Severability

If any provision of these Terms is found invalid or unenforceable, that provision will be limited to the minimum extent necessary, and the remaining provisions will continue in full force and effect.

## 16. Entire Agreement

These Terms, together with our Privacy Policy and any applicable Engagement Agreement, constitute the entire agreement between you and BStrong Performance regarding your use of the Service.

## 17. Waiver

Our failure to enforce any provision of these Terms does not constitute a waiver of our right to enforce it in the future.

## 18. Assignment

You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations in connection with a merger, acquisition, or sale of assets.

## 19. Contact Information

For questions, concerns, or legal notices regarding these Terms, please contact us:

**BStrong Performance**
Email: brian@BStrongPerformance.com

Please include "Terms of Service Inquiry" in the subject line of any legal notices.

---

**By using our Service or engaging BStrong Performance, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.**`;

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div
          className="terms-container"
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "2rem",
            lineHeight: "1.6",
          }}
        >
          <ReactMarkdown
            components={{
              h1: ({ ...props }) => (
                <h1
                  className="font_family_base"
                  style={{
                    fontSize: "2.5rem",
                    marginBottom: "1rem",
                    borderBottom: "2px solid #737373",
                    paddingBottom: "0.5rem",
                  }}
                  {...props}
                />
              ),
              h2: ({ ...props }) => (
                <h2
                  className="font_family_base header_text"
                  style={{
                    fontSize: "1.8rem",
                    marginTop: "2rem",
                    marginBottom: "1rem",
                    // color: "#00bf62",
                  }}
                  {...props}
                />
              ),
              h3: ({ ...props }) => (
                <h3
                  className="font_family_base detail_text"
                  style={{
                    fontSize: "1.4rem",
                    marginTop: "1.5rem",
                    marginBottom: "0.75rem",
                  }}
                  {...props}
                />
              ),
              p: ({ ...props }) => (
                <p
                  className="font_family_base detail_text"
                  style={{ marginBottom: "1rem" }}
                  {...props}
                />
              ),
              ul: ({ ...props }) => (
                <ul
                  className="font_family_base detail_text"
                  style={{ marginLeft: "2rem", marginBottom: "1rem" }}
                  {...props}
                />
              ),
              li: ({ ...props }) => (
                <li
                  className="font_family_base detail_text"
                  style={{ marginBottom: "0.5rem" }}
                  {...props}
                />
              ),
              strong: ({ ...props }) => (
                <strong
                  className="font_family_base header_text"
                  // style={{ color: "#00bf62" }}
                  {...props}
                />
              ),
              hr: ({ ...props }) => (
                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid #737373",
                    margin: "2rem 0",
                  }}
                  {...props}
                />
              ),
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>
      <Footer />
    </div>
  );
}
